/**
 * Analytics Provider — pluggable abstraction over analytics backends.
 *
 * Supports PostHog and GA4 out of the box.  Falls back to a noop provider
 * when no keys are configured (safe for build-time and local dev).
 *
 * Usage:
 *   import { analytics } from "@/lib/analytics";
 *   analytics.track("click_install", { platform: "ios", source: "hero" });
 */

import type { EventName, EventProperties, EventContext } from "./events";

/* -------------------------------------------------------------------------- */
/*  Provider interface                                                         */
/* -------------------------------------------------------------------------- */

export interface AnalyticsProvider {
  /** Identify a known user (admin, author, etc.). */
  identify(userId: string, traits?: Record<string, unknown>): void;

  /** Track a typed event. */
  track<N extends EventName>(name: N, properties: EventProperties<N>): void;

  /** Track a page view. */
  page(properties: EventContext & { path: string; title?: string }): void;

  /** Reset / anonymize the current user session. */
  reset(): void;

  /** Whether this provider is actually sending data. */
  readonly enabled: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Noop provider (default when no analytics backend is configured)            */
/* -------------------------------------------------------------------------- */

class NoopProvider implements AnalyticsProvider {
  readonly enabled = false;

  identify(): void {
    /* noop */
  }

  track(): void {
    /* noop */
  }

  page(): void {
    /* noop */
  }

  reset(): void {
    /* noop */
  }
}

/* -------------------------------------------------------------------------- */
/*  PostHog provider                                                           */
/* -------------------------------------------------------------------------- */

class PostHogProvider implements AnalyticsProvider {
  readonly enabled = true;

  identify(userId: string, traits?: Record<string, unknown>): void {
    if (typeof window === "undefined") return;
    const ph = getPostHog();
    ph?.identify(userId, traits);
  }

  track<N extends EventName>(name: N, properties: EventProperties<N>): void {
    if (typeof window === "undefined") return;
    const ph = getPostHog();
    ph?.capture(name, properties as unknown as Record<string, unknown>);
  }

  page(properties: EventContext & { path: string; title?: string }): void {
    if (typeof window === "undefined") return;
    const ph = getPostHog();
    ph?.capture("$pageview", properties as unknown as Record<string, unknown>);
  }

  reset(): void {
    if (typeof window === "undefined") return;
    const ph = getPostHog();
    ph?.reset();
  }
}

/** Lazily get the PostHog global (loaded via script tag or npm). */
function getPostHog(): PostHogLike | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).posthog as PostHogLike | undefined;
}

interface PostHogLike {
  identify(userId: string, traits?: Record<string, unknown>): void;
  capture(event: string, properties?: Record<string, unknown>): void;
  reset(): void;
}

/* -------------------------------------------------------------------------- */
/*  GA4 provider (using gtag global)                                           */
/* -------------------------------------------------------------------------- */

class GA4Provider implements AnalyticsProvider {
  readonly enabled = true;

  identify(userId: string): void {
    if (typeof window === "undefined") return;
    getGtag()?.("config", getGA4Id(), { user_id: userId });
  }

  track<N extends EventName>(name: N, properties: EventProperties<N>): void {
    if (typeof window === "undefined") return;
    getGtag()?.('event', name, properties as unknown as Record<string, unknown>);
  }

  page(properties: EventContext & { path: string; title?: string }): void {
    if (typeof window === "undefined") return;
    getGtag()?.("event", "page_view", {
      page_path: properties.path,
      page_title: properties.title,
      ...properties,
    });
  }

  reset(): void {
    /* GA4 does not have a reset concept like PostHog. */
  }
}

function getGtag(): GtagLike | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).gtag as GtagLike | undefined;
}

function getGA4Id(): string {
  return process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID ?? "";
}

type GtagLike = (
  command: string,
  targetOrEvent: string,
  params?: Record<string, unknown>,
) => void;

/* -------------------------------------------------------------------------- */
/*  Multi-provider (sends to all configured backends)                          */
/* -------------------------------------------------------------------------- */

class MultiProvider implements AnalyticsProvider {
  private readonly providers: AnalyticsProvider[];

  constructor(providers: AnalyticsProvider[]) {
    this.providers = providers.filter((p) => p.enabled);
  }

  get enabled(): boolean {
    return this.providers.length > 0;
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    this.providers.forEach((p) => p.identify(userId, traits));
  }

  track<N extends EventName>(name: N, properties: EventProperties<N>): void {
    this.providers.forEach((p) => p.track(name, properties));
  }

  page(properties: EventContext & { path: string; title?: string }): void {
    this.providers.forEach((p) => p.page(properties));
  }

  reset(): void {
    this.providers.forEach((p) => p.reset());
  }
}

/* -------------------------------------------------------------------------- */
/*  Factory                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Build the analytics instance based on environment variables.
 *
 * PostHog key: `NEXT_PUBLIC_POSTHOG_KEY`
 * GA4 ID: `NEXT_PUBLIC_GA4_MEASUREMENT_ID`
 */
export function createAnalytics(): AnalyticsProvider {
  const providers: AnalyticsProvider[] = [];

  if (process.env.NEXT_PUBLIC_POSTHOG_KEY) {
    providers.push(new PostHogProvider());
  }

  if (process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID) {
    providers.push(new GA4Provider());
  }

  if (providers.length === 0) return new NoopProvider();
  if (providers.length === 1) return providers[0]!;
  return new MultiProvider(providers);
}

/** Singleton analytics instance. */
export const analytics: AnalyticsProvider = createAnalytics();
